import React from "react";
import { useNavigate } from "react-router-dom";
import { Car, Smartphone, Link as LinkIcon, AlertCircle } from "lucide-react";
import AdBanner from "../components/AdBanner";
import useSeo from "../seo/useSeo";

export default function UserManual() {
  useSeo("/user-manual");
  const navigate = useNavigate();

  return (
    <div className="min-h-[100dvh] bg-slate-950 text-white pb-24">
      {/* Header */}
      <div className="bg-slate-900 border-b border-slate-800 p-4 sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button
            onClick={() => window.history.length > 1 ? window.history.back() : navigate("/")}
            className="p-2 -ml-2 rounded-full hover:bg-slate-800 transition-colors"
          >
            ←
          </button>
          <h1 className="text-lg font-bold">User Manual</h1>
        </div>
      </div>

      <div className="p-5 space-y-8 max-w-2xl mx-auto">
        <div className="space-y-2">
          <h2 className="text-2xl font-black text-primary">How CarTrack Works</h2>
          <p className="text-slate-400 text-sm leading-relaxed">
            CarTrack is built on a professional IoT (Internet of Things) architecture. It consists of two separate applications: the <strong>Car Head Unit Tracker</strong> and the <strong>Owner Dashboard</strong> (the app you are currently using).
          </p>
        </div>

        {/* Step 1 */}
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <Car size={80} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">1</div>
            <h3 className="text-lg font-bold">Install the Tracker in your Car</h3>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed mb-4">
            The tracking engine runs completely in the background on your Android car head unit.
          </p>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>During onboarding, click <strong>Download Car Head Unit APK</strong>, then transfer it via USB or Bluetooth to your car's head unit. You can also find this link in your Settings.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>Open the app on your car screen and grant <strong>Location</strong> and <strong>Background</strong> permissions.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>Write down the <strong>Device ID</strong> shown on the car's screen (e.g. <code>CT-A1B2-C3D4</code>).</span>
            </li>
          </ul>
        </div>

        {/* Step 2 */}
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <LinkIcon size={80} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">2</div>
            <h3 className="text-lg font-bold">Link Your Device</h3>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed mb-4">
            Connect the car to your owner account to start receiving live updates and SOS alerts.
          </p>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>Open this Mobile Web App on your phone.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>Go to the <strong>Devices</strong> tab.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>Click <strong>Add Device</strong> and enter the Device ID from your car's screen.</span>
            </li>
          </ul>
        </div>

        {/* Step 3 */}
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800 relative overflow-hidden">
          <div className="absolute top-0 right-0 p-4 opacity-5">
            <Smartphone size={80} />
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-primary/20 text-primary flex items-center justify-center font-bold">3</div>
            <h3 className="text-lg font-bold">Monitor & Setup Alerts</h3>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed mb-4">
            Now that your car is linked, you can configure Telegram alerts for SOS and Geofences.
          </p>
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>Go to <strong>Settings</strong> to enter your Telegram Chat ID (This is <strong>mandatory</strong> to receive alerts).</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>Create <strong>Geofences</strong> to be notified when the car enters or leaves specific areas.</span>
            </li>
            <li className="flex gap-2">
              <span className="text-primary">•</span>
              <span>If the driver presses the <strong>SOS button</strong> on the car screen, you will instantly receive a Telegram alert with a live tracking link.</span>
            </li>
          </ul>
        </div>

        {/* Managing Account */}
        <div className="bg-slate-900 rounded-2xl p-5 border border-slate-800 relative overflow-hidden">
          <div className="flex items-center gap-3 mb-4">
            <h3 className="text-lg font-bold">Managing Your Account</h3>
          </div>
          <p className="text-slate-400 text-sm leading-relaxed mb-4">
            You can manage your data completely from the <strong>Settings</strong> page. If you no longer wish to use CarTrack, you can use the <strong>Delete Account</strong> feature. This permanently deletes your account, unlinks all devices, and purges all tracking history to comply with global privacy standards.
          </p>
        </div>

        {/* Troubleshooting */}
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-5">
          <div className="flex items-center gap-2 text-red-400 mb-3 font-bold">
            <AlertCircle size={20} />
            <span>Troubleshooting</span>
          </div>
          <p className="text-sm text-slate-300 leading-relaxed">
            If the car's location is not updating, ensure the head unit has an active internet connection (WiFi or 4G) and that the CarTrack background service was not killed by the Android system's battery optimizer.
          </p>
        </div>

        <div className="pt-4">
          <AdBanner testid="manual-ad-banner" />
        </div>
      </div>
    </div>
  );
}
